import isMobile from "is-mobile";
import { getUrlWithVariant } from "@shopify/theme-product-form";
import { qs, qsa, add, remove, listen } from "@fluorescent/dom";
import { slideUp, slideDown, slideStop } from "slide-anim";

import { cart, section } from "@/glow";
import { emit } from "@/glow/events";
import ProductForm from "@/lib/productForm";
import Media from "@/lib/media";
import { updateRecentProducts } from "@/lib/recently-viewed";
import storeAvailability from "@/lib/store-availability";
import Lightbox from "@/lib/lightbox";
import Accordions from "@/lib/accordion";
import { wrapIframes, wrapTables } from "@/lib/rte";

import {
  createSwatchGroups,
  getProduct,
  updateBuyButton,
  updateMedia,
  updatePrices,
  updateUnitPrices,
  updateSku,
  inventoryCounter,
  reviewsHandler,
} from "@/lib/product";
import selectors from "@/lib/selectors";
import variantPopup from "@/lib/variant-popup";

const { product: selector } = selectors;

section("product", {
  productForm: null,
  swatchGroups: [],
  availability: null,
  events: [],
  reviewsHandler: null,

  //
  //
  //
  //
  onLoad() {
    this.media = Media(this.container);
    const { enableAjax } = this.container.dataset;

    const inYourSpaceButton = qs(
      ".bttn.product__in-space-bttn",
      this.container
    );

    if (isMobile() && Boolean(inYourSpaceButton)) {
      add(inYourSpaceButton, "visible");
    }

    this.formElement = qs(selector.form, this.container);
    this.isProductPage = !Boolean(this.container.dataset.isDynamic);
    if (!this.formElement) return;

    const { productHandle } = this.formElement.dataset;

    this.quantityError = qs("[data-quantity-error]", this.container);

    this.storeAvailabilityContainer = qs(
      selector.storeAvailability,
      this.formElement
    );

    const productJSON = getProduct(productHandle);

    productJSON(data => {
      const options = {
        variantSelector: "#variant-selector",
        onOptionChange: e => this.onOptionChange(e),
      };

      if (enableAjax) {
        options.onFormSubmit = e => this.onFormSubmit(e);
      }

      this.productForm = ProductForm(this.formElement, data, options);

      if (this.isProductPage) {
        this._storeView(data);
        this._initLightbox();
      }

      updatePrices.call(this, this.productForm.getVariant());

      // Surface pickup
      const variant = this.productForm.getVariant();

      if (this.storeAvailabilityContainer && variant) {
        this.availability = storeAvailability(
          this.storeAvailabilityContainer,
          data,
          variant
        );
      }

      const productInventoryJson = qs(
        "[data-product-inventory-json]",
        this.container
      );

      if (productInventoryJson) {
        const jsonData = JSON.parse(productInventoryJson.innerHTML);
        const variantsInventories = jsonData.inventory;

        if (variantsInventories) {
          const config = {
            id: variant.id,
            variantsInventories,
          };
          this.inventoryCounter = inventoryCounter(this.container, config);
        }
      }
    });

    this.isSlider = false;

    this._handleSwitch = this._handleSwitch.bind(this);
    this._initImageSwitching();
    this._initImageCarousel();

    updateMedia.call(
      this,
      this.container.querySelector("[data-product-initial-image]").dataset
        .productInitialImage,
      false
    );

    this.swatches = createSwatchGroups(
      this.container,
      "[data-product-swatches]"
    );
    this.variantPopup = variantPopup(this.container);

    const accordions = qsa(".accordion", this.container);

    if (accordions.length) {
      this.accordions = Accordions(accordions);
    }

    window.SPRCallbacks = {};

    window.SPRCallbacks.onReviewsLoad = () => {
      if (!this.reviewsHandler) {
        this.reviewsHandler = reviewsHandler();
      }
    };

    const productDescriptionWrapper = qs(
      ".product__description",
      this.container
    );

    if (productDescriptionWrapper) {
      wrapIframes(qsa("iframe", productDescriptionWrapper));
      wrapTables(qsa("table", productDescriptionWrapper));
    }
  },

  //
  //
  //
  //
  onUnload() {
    this.productForm && this.productForm.destroy();
    this.swatches && this.swatches();
    this.accordions && this.accordions.unload();
    this.variantPopup && this.variantPopup.unload();

    if (this.isSlider) {
      this.thumbnailSlider && this.thumbnailSlider.destroy();
    }

    this.events.forEach(unsubsribe => unsubsribe());
  },

  //
  //
  // A callback method that is fired whenever the user changes the value of an option input.
  onOptionChange(event) {
    const { variant } = event.dataset;

    // Update sku
    updateSku(this.container, variant);

    // Update inventory counter
    this.inventoryCounter && this.inventoryCounter.update(variant);

    if (!variant) {
      updateBuyButton.call(this, { available: false });

      if (this.availability && this.storeAvailabilityContainer) {
        this.availability.unload();
      }

      return;
    }

    // We only want to update the URL on the product page
    if (!Boolean(this.container.dataset.isDynamic)) {
      const url = getUrlWithVariant(window.location.href, variant.id);
      window.history.replaceState({ path: url }, "", url);
    }

    // We need to set the id input manually so the Dynamic Checkout Button works
    const selectedVariantOpt = qs(
      `${selector.variantSelect} ${selector.optionById(variant.id)}`,
      this.container
    );
    selectedVariantOpt.selected = true;

    // We need to dispatch an event so Shopify pay knows the form has changed
    this.formElement.dispatchEvent(new Event("change"));

    variant.featured_media &&
      updateMedia.call(this, variant.featured_media.id, true);
    updatePrices.call(this, variant);
    updateBuyButton.call(this, variant);

    // Update unit pricing
    updateUnitPrices(this.container, variant);

    this.availability && this.availability.update(variant);
  },

  //
  //
  //
  //
  onFormSubmit(evt) {
    evt.preventDefault();

    const buttons = qsa(selector.addButton, document);
    buttons.forEach(el => add(el, "bttn--loading"));

    add(this.quantityError, "hidden");

    cart
      .addItem(this.formElement)
      .then(() => {
        emit("cart:toggle", state => {
          return {
            cartOpen: !state.cartOpen,
          };
        });
        buttons.forEach(el => remove(el, "bttn--loading"));
      })
      .catch(() => {
        remove(this.quantityError, "hidden");
        buttons.forEach(el => remove(el, "bttn--loading"));
      });
  },

  //
  //
  //
  //
  _initImageSwitching() {
    this.events.push(
      listen(qsa(selector.thumb, this.container), "click", this._handleSwitch)
    );
  },

  //
  //
  //
  //
  _initLightbox() {
    this.images = qsa(".product__image", this.container);

    const lightbox = this.container.querySelector("[data-lightbox]");

    this.lightbox = Lightbox(lightbox);

    this.images.forEach(image => {
      this.events.push(
        listen(image, "click", this._handleImageClick.bind(this))
      );
    });
  },

  _handleImageClick(e) {
    e.preventDefault();
    this.lightbox.open(e.currentTarget.dataset.open);
  },

  //
  //
  //
  //
  _initImageCarousel() {
    const sliderEl = qs(selector.thumbs, this.container);

    if (!sliderEl) return;

    import(flu.chunks.flickity).then(({ Flickity }) => {
      this.thumbnailSlider = new Flickity(sliderEl, {
        adaptiveHeight: true,
        cellAlign: "left",
        on: {
          ready: () => {
            this.isSlider = true;
          },
        },
        pageDots: false,
        prevNextButtons: false,
        watchCSS: true,
      });
    });
  },

  //
  //
  //
  //
  _handleSwitch(evt) {
    evt.preventDefault();

    const {
      currentTarget: { dataset },
    } = evt;

    this.media && this.media.pauseActiveMedia();

    updateMedia.call(this, dataset.thumbnailId, true);
  },

  //
  //
  //
  //
  _storeView: updateRecentProducts,

  onBlockSelect({ target }) {
    const label = qs(".accordion__label", target);
    target.scrollIntoView({ block: "center", behavior: "smooth" });

    if (!label) return;
    const { parentNode: group, nextElementSibling: content } = label;

    slideStop(content);
    slideDown(content);
    group.setAttribute("data-open", true);
    label.setAttribute("aria-expanded", true);
    content.setAttribute("aria-hidden", false);
  },

  onBlockDeselect({ target }) {
    const label = qs(".accordion__label", target);
    if (!label) return;
    const { parentNode: group, nextElementSibling: content } = label;

    slideStop(content);
    slideUp(content);
    group.setAttribute("data-open", false);
    label.setAttribute("aria-expanded", false);
    content.setAttribute("aria-hidden", true);
  },
});
